import { useState } from 'react';

import DeleteModal from '@atom/components/common/DeleteModal';
import RenameModal from '@atom/components/common/RenameModal';
import { Icon, IconButton } from '@atom/mui';
import {
  ArrowBackIcon,
  DeleteIcon,
  EditIcon,
  FileDownloadIcon,
  InfoIcon,
  StarIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { Media } from '@atom/types/media';
import { FILES_ENDPOINT } from '@atom/utilities/endpoints';

import classes from './carousel.module.css';

interface Props {
  onClose: () => void;
  openSideBar: () => void;
  selectedMedia: Media;
  deleteMedia: (mediaId: string) => void;
  renameMedia: (mediaId: string, subjectId: string, name: string) => void;
  subjectId: string;
  setAsPrimaryMedia?: (mediaId: string) => void;
  primaryMediaId?: string;
  canMarkAsCoverPhoto?: boolean;
  canUpdateMedia: boolean;
  canDeleteMedia: boolean;
}

const CarouselHeader = ({
  onClose,
  openSideBar,
  selectedMedia,
  deleteMedia,
  subjectId,
  renameMedia,
  setAsPrimaryMedia,
  primaryMediaId,
  canMarkAsCoverPhoto,
  canUpdateMedia,
  canDeleteMedia,
}: Props) => {
  const [renameOpen, setRenameOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

  const renameAction = (mediaId: string, name: string) => {
    renameMedia(mediaId, subjectId, name);
    setRenameOpen(false);
  };

  const handleDelete = () => {
    deleteMedia(selectedMedia.id);
    setDeleteOpen(false);
  };

  const primaryPhotoText =
    primaryMediaId === selectedMedia.id ||
    primaryMediaId === selectedMedia.fileId
      ? 'Unmark as Cover Photo'
      : 'Mark as Cover Photo';

  const primaryPhotoColor =
    primaryMediaId === selectedMedia.id ||
    primaryMediaId === selectedMedia.fileId
      ? colors.brand.blue
      : colors.neutral.white;

  return (
    <div className={classes.modalTopToolbar}>
      <div className={classes.backButtonContainer}>
        <IconButton onClick={onClose}>
          <Icon color={colors.neutral.white} icon={ArrowBackIcon} />
        </IconButton>
        <div
          className={classes.fileName}
        >{`${selectedMedia.name}.${selectedMedia.fileExtension}`}</div>
      </div>
      <div className={classes.toolbarButtonContainer}>
        {canUpdateMedia && (
          <>
            <IconButton onClick={() => setRenameOpen(true)} tooltip="Rename">
              <Icon color={colors.neutral.white} icon={EditIcon} />
            </IconButton>
            <RenameModal
              id={selectedMedia.id}
              name={selectedMedia.name || ''}
              type="media"
              renameAction={renameAction}
              open={renameOpen}
              closeModal={() => setRenameOpen(false)}
            />
          </>
        )}
        <a
          href={`${FILES_ENDPOINT}/${selectedMedia.fileId}`}
          download={`${selectedMedia.name}.${selectedMedia.fileExtension}`}
        >
          <IconButton tooltip="Download">
            <Icon color={colors.neutral.white} icon={FileDownloadIcon} />
          </IconButton>
        </a>
        {setAsPrimaryMedia && canMarkAsCoverPhoto && (
          <IconButton
            onClick={() => setAsPrimaryMedia(selectedMedia.id)}
            tooltip={primaryPhotoText}
          >
            <Icon color={primaryPhotoColor} icon={StarIcon} />
          </IconButton>
        )}
        {canDeleteMedia && (
          <>
            <IconButton onClick={() => setDeleteOpen(true)} tooltip="Delete">
              <Icon color={colors.neutral.white} icon={DeleteIcon} />
            </IconButton>
            <DeleteModal
              title="Delete File"
              open={deleteOpen}
              onCancel={() => setDeleteOpen(false)}
              onConfirm={handleDelete}
              confirmText="Delete"
              content="Are you sure you want to delete this file?"
            />
          </>
        )}
        <IconButton onClick={openSideBar} tooltip="Information">
          <Icon color={colors.neutral.white} icon={InfoIcon} />
        </IconButton>
      </div>
    </div>
  );
};

export default CarouselHeader;
